import { useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/components/ui/Toast'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Sheet } from '@/components/ui/Sheet'
import { METHOD_LABELS } from '@/data/dues'
import type { PaymentMethod } from '@/data/dues'
import { recordSession } from '@/data/income'
import type { IncomeFund, SessionLineInput } from '@/data/income'
import { formatKwacha, parseKwacha } from '@/lib/money'
import { lastSabbath, lusakaToday, weekdayOf } from '@/lib/periods'

const METHODS: PaymentMethod[] = ['cash', 'mobile_money', 'bank_transfer']
const newId = () => (globalThis.crypto?.randomUUID ? globalThis.crypto.randomUUID() : `00000000-0000-4000-8000-${Date.now().toString(16).padStart(12, '0').slice(-12)}`)
const errText = (e: unknown) => (e instanceof Error ? e.message : 'Something went wrong. Please try again.')

type Cell = Partial<Record<PaymentMethod, string>>

/** Count a Sabbath: who counted, and how much of each fund came in by each method. */
export function CountForm({ funds, isOpen, onClose }: { funds: IncomeFund[]; isOpen: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const today = lusakaToday()
  const [serviceOn, setServiceOn] = useState(lastSabbath(today))
  const [counter1, setCounter1] = useState('')
  const [counter2, setCounter2] = useState('')
  const [label, setLabel] = useState('')
  const [note, setNote] = useState('')
  const [reference, setReference] = useState('')
  const [cells, setCells] = useState<Record<string, Cell>>({})
  const [error, setError] = useState('')
  const busy = useRef(false)
  // One id per time the form is opened: a retry whose first answer was lost finds the count it made.
  const countId = useRef(newId())
  const toast = useToast()
  const save = useMutation({
    mutationFn: (input: Parameters<typeof recordSession>[0]) => recordSession(input, countId.current),
  })

  // Reset every time it opens (the parent keeps this mounted so the id survives a retry).
  const wasOpen = useRef(false)
  if (isOpen && !wasOpen.current) {
    wasOpen.current = true
    countId.current = newId()
  }
  if (!isOpen && wasOpen.current) wasOpen.current = false

  const setCell = (fundId: string, m: PaymentMethod, v: string) =>
    setCells((c) => ({ ...c, [fundId]: { ...c[fundId], [m]: v } }))

  const parsed = funds.flatMap((f) =>
    METHODS.map((m) => {
      const raw = (cells[f.id]?.[m] ?? '').trim()
      return { fund: f, method: m, raw, ngwee: raw === '' ? 0 : parseKwacha(raw) }
    }),
  )
  const total = parsed.reduce((s, p) => s + (p.ngwee && p.ngwee > 0 ? p.ngwee : 0), 0)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (busy.current) return
    setError('')
    if (!serviceOn) return setError('Choose the date of the service.')
    if (serviceOn > today) return setError('The date cannot be in the future.')
    if (counter1.trim().length < 2 || counter2.trim().length < 2) return setError('Enter the names of both people who counted.')
    if (counter1.trim().toLowerCase() === counter2.trim().toLowerCase()) return setError('The two counters must be two different people.')
    const bad = parsed.find((p) => p.raw !== '' && (p.ngwee === null || p.ngwee < 0))
    if (bad) return setError(`Check the amount for ${bad.fund.name} (${METHOD_LABELS[bad.method]}).`)
    const lines: SessionLineInput[] = parsed
      .filter((p) => p.ngwee && p.ngwee > 0)
      .map((p) => ({
        fundId: p.fund.id,
        amountNgwee: p.ngwee as number,
        method: p.method,
        reference: p.method === 'cash' ? undefined : reference,
      }))
    if (lines.length === 0) return setError('Enter at least one amount.')
    busy.current = true
    try {
      await save.mutateAsync({ serviceOn, counter1, counter2, label, note, lines })
      await qc.invalidateQueries({ queryKey: ['income'] })
      toast.success('Count saved. Now add a photo of the signed count sheet.')
      setCells({}); setCounter1(''); setCounter2(''); setLabel(''); setNote(''); setReference('')
      onClose()
    } catch (err) {
      setError(errText(err))
    } finally {
      busy.current = false
    }
  }

  return (
    <Sheet isOpen={isOpen} onClose={onClose} title="Count a Sabbath">
      <form onSubmit={(e) => void submit(e)} className="flex flex-col gap-4" noValidate>
        <Input
          label="Date of the service"
          type="date"
          max={today}
          value={serviceOn}
          onChange={(e) => setServiceOn(e.target.value)}
          hint={serviceOn && weekdayOf(serviceOn) !== 6 ? 'That day is not a Saturday. Check it is the right date.' : undefined}
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Input label="First counter" value={counter1} onChange={(e) => setCounter1(e.target.value)} autoComplete="off" />
          <Input label="Second counter" value={counter2} onChange={(e) => setCounter2(e.target.value)} autoComplete="off" />
        </div>

        <fieldset className="flex flex-col gap-3">
          <legend className="text-body font-medium text-neutral">Amounts in Kwacha (leave blank for none)</legend>
          {funds.map((f) => (
            <div key={f.id} className="rounded-control border border-line p-3">
              <p className="mb-2 font-medium text-ink">{f.name}</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {METHODS.map((m) => (
                  <Input
                    key={m}
                    label={METHOD_LABELS[m]}
                    inputMode="decimal"
                    placeholder="0.00"
                    value={cells[f.id]?.[m] ?? ''}
                    onChange={(e) => setCell(f.id, m, e.target.value)}
                  />
                ))}
              </div>
            </div>
          ))}
        </fieldset>
        <Input label="Mobile money or bank reference (optional)" value={reference} onChange={(e) => setReference(e.target.value)} hint="Applied to the mobile money and bank amounts above." />
        <Input label="Name of the service (optional)" placeholder="Sabbath service" value={label} onChange={(e) => setLabel(e.target.value)} />
        <Textarea label="Note (optional)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} />

        <p className="text-section font-semibold text-ink" aria-live="polite">Total counted: {formatKwacha(total)}</p>
        {error && <p role="alert" className="text-body text-bad">{error}</p>}
        <Button type="submit" size="lg" isLoading={save.isPending}>{save.isPending ? 'Saving...' : 'Save the count'}</Button>
        <p className="text-body text-muted">After saving, add a photo of the signed count sheet.</p>
      </form>
    </Sheet>
  )
}
